import * as React from 'react';
import { debounce } from 'perfect-debounce';
import { accentUtils } from '../../services/HelperService';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { ConfiguratorContext } from './ConfigContextProvider';
import { useValidation, setOptionValueChanged, createDeferredOptionValueChange, isReadOnly, toGuid, getOptionModel, useRefreshKeyExpression, optionAccessibleName } from './ProductConfiguratorShared';


function picklistValue(model) {
    return `${model.CurrentValueAsString ?? ''}_${model.CustomValue ?? ""}`;
}

const EMPTY_GUID_PATTERN = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;
const isBlankChoice = x => !x.ID || EMPTY_GUID_PATTERN.test(`${x.ID}`);

// Measure the actual labels with the same button styles; character counts do not
// account for translations, font changes or the width of a multiline grid cell.
// The blank ("none") value is not a button: clicking the selected button clears the
// selection when the option allows a blank. The dropdown fallback keeps the blank option.
// Each button is as wide as its label; more than three choices always use the dropdown.
const BUTTON_MAX_CHOICES = 3;

// The option row the picklist shares with its action buttons. Once the buttons are shown
// the picklist shrinks to fit them, so its own box no longer says how much room there is:
// measure the row minus everything else in it (browse button, custom-value text box) instead.
// Outside an option row (tests, other hosts) the picklist's own width is the available width.
const ROW_SELECTOR = ".config-option-value-container";

function availableWidth(element) {
    if (!element) return 0;
    const row = element.closest(ROW_SELECTOR);
    if (!row) return element.clientWidth;
    let used = 0;
    for (let node = element; node !== row && node.parentElement; node = node.parentElement) {
        const parent = node.parentElement;
        const gap = parseFloat(window.getComputedStyle(parent).columnGap) || 0;
        for (const sibling of parent.children) {
            if (sibling !== node) used += sibling.getBoundingClientRect().width + gap;
        }
    }
    return row.clientWidth - used;
}

export const AdaptivePicklist = ({ data, selectedValue, onChange, readOnly, required, invalid, label, small }) => {
    const container = React.useRef(null);
    const measure = React.useRef(null);
    const [fits, setFits] = React.useState(false);
    const blank = data.find(isBlankChoice);
    const choices = data.filter(x => !isBlankChoice(x)).map(x => ({ ...x, Value: `${x.Value ?? ""}`.trim() || `${x.ID}` }));
    const canUseButtons = choices.length > 0 && choices.length <= BUTTON_MAX_CHOICES;

    React.useLayoutEffect(() => {
        const update = () => {
            const available = availableWidth(container.current);
            const needed = measure.current?.scrollWidth ?? 0;
            setFits(canUseButtons && available > 0 && needed > 0 && needed + 2 <= available);
        };
        update();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
        // Everything from the picklist up to its row: a custom-value box appearing beside the
        // buttons changes the room left for them without changing the row itself.
        const row = container.current?.closest(ROW_SELECTOR);
        for (let node = container.current; node; node = node.parentElement) {
            observer?.observe(node);
            if (!row || node === row) break;
        }
        if (measure.current) observer?.observe(measure.current);
        window.addEventListener("resize", update);
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [canUseButtons, data, small]);

    const select = id => onChange({ target: { value: id } });
    // Clicking the selected choice clears it when a blank value exists; otherwise it is a no-op.
    const toggle = id => {
        if (id !== selectedValue) select(id);
        else if (blank) select(blank.ID);
    };
    const onKeyDown = (event, index) => {
        let next;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (index + 1) % choices.length;
        if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (index + choices.length - 1) % choices.length;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = choices.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        select(choices[next].ID);
        event.currentTarget.parentElement.children[next].focus();
    };
    const selectedIndex = choices.findIndex(x => x.ID === selectedValue);
    return <div className={`config-picklist-adaptive ${small ? "small" : ""}`} ref={container}>
        {canUseButtons && <div ref={measure} className="config-picklist-measure" aria-hidden="true">
            {choices.map(x => <span className="config-choice" key={x.ID}>{x.Value}</span>)}
        </div>}
        {fits && canUseButtons ? <div className={`config-picklist-buttons ${invalid ? "required" : ""}`}
            role="radiogroup" aria-label={label} aria-required={required || undefined} aria-invalid={invalid || undefined}>
            {choices.map((x, index) => <button key={x.ID} type="button" role="radio"
                className="config-choice" aria-checked={x.ID === selectedValue} disabled={readOnly}
                title={blank && x.ID === selectedValue ? "Click to clear" : undefined}
                tabIndex={index === (selectedIndex < 0 ? 0 : selectedIndex) ? 0 : -1}
                onKeyDown={event => onKeyDown(event, index)} onClick={() => toggle(x.ID)}>{x.Value}</button>)}
        </div> : <select className={`config-select ${invalid ? "required" : ""}`} onChange={onChange}
            disabled={readOnly} value={selectedValue} aria-label={label}
            aria-required={required || undefined} aria-invalid={invalid || undefined}>
            {data.map(x => <option key={x.ID} value={x.ID}>{x.Value}</option>)}
        </select>}
    </div>;
};

export const ConfigPicklist = props => {

    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, props.optionID);

    const updateSpecify = React.useRef(debounce(setOptionValueChanged, 800, { trailing: false }));

    // The selection highlight is React-controlled, so paint it before the cascade runs.
    const applySelection = React.useRef(createDeferredOptionValueChange());

    const validation = useValidation(props.optionID, "ConfigPicklist");

    const [value, setValue] = React.useState(picklistValue(optionModel));

    const refreshKey = useRefreshKeyExpression(props.optionID, picklistValue, value);

    const [customValue, setCustomValue] = React.useState(optionModel.CustomValue);

    const onTextChange = React.useCallback(e => {
        setCustomValue(e);

        if (ctx.notifyDirty) {
            ctx.notifyDirty();
        }

    }, [ctx]);

    const onTextBlur = React.useCallback(e => {
        let newValue = e;
        let valID = null;
        if (optionModel.OrderLineOption.ProductOptionValueID != toGuid(accentUtils.getEmptyGuid())) {

            const pov = optionModel.GetProductOptionValue(optionModel.OrderLineOption.ProductOptionValueID);

            if (pov != null) {
                newValue = `${pov.Description}: ${e}`;

                valID = pov.ID.toString();
            }
        }

        if (optionModel.OrderLineOption != null) {
            optionModel.OrderLineOption.CustomValue = newValue;
        }

        updateSpecify.current(ctx, optionModel);

        setValue(`${valID ?? ""}_${newValue ?? ""}`);
    }, [ctx, optionModel, updateSpecify]);


    const data = React.useMemo(() => optionModel.FilteredItems.toArray().map(x => ({ ID: x.ItemKey.toString().toUpperCase(), Value: x.ItemValue })), [optionModel, refreshKey.refreshID]);

    const onChange = React.useCallback(e => {
        const id = toGuid(e.target.value);

        const value = optionModel.GetProductOptionValue(id);

        optionModel.SetValue(window.InsyteConfig.OptionModelSetValueArgs.From(id, value?.Description?? null));

        applySelection.current(ctx, optionModel);


        if (accentUtils.isNull(value) || (value?.CustomValue??false)) {
            setCustomValue(null);
        }

        setValue(`${id?.toString() ?? ""}_${""}`);
    }, [ctx, optionModel, setCustomValue]);

    

    const readOnly = isReadOnly(optionModel);
    

    const pov = optionModel.GetProductOptionValue(optionModel.OrderLineOption.ProductOptionValueID);

    const isCusomValue = accentUtils.isNull(pov) ? false : pov.CustomValue;

    const currValue = optionModel.CurrentValueAsString?.toUpperCase();
    const emptyGuid = accentUtils.getEmptyGuid().toUpperCase();
    const hasCurrentValue = data.some(x => x.ID === currValue);
    const hasEmptyGuidOption = data.some(x => x.ID === emptyGuid);
    const selectedValue = hasCurrentValue ? currValue : (hasEmptyGuidOption ? emptyGuid : "");

    const isRequired = validation.required && accentUtils.isEmpty(currValue);


    return <div className="config-control config-picklist">
        <div className="config-picklist-picklist">
            <AdaptivePicklist
                data={data}
                onChange={onChange}
                readOnly={readOnly}
                selectedValue={selectedValue}
                label={optionAccessibleName(optionModel)}
                required={validation.required}
                invalid={isRequired}
                small={props.small}
            />
        </div>
        {isCusomValue && <div className="config-picklist-custom">
            <AccentTextUnbound
                key={`${refreshKey.refreshID}`}
                required={validation.required}
                defaultValue={customValue}
                onChange={onTextChange}
                onBlur={onTextBlur}
                disabled={readOnly}
                autoSize
                ariaLabel={optionAccessibleName(optionModel, "custom value")}
            />
        </div>}
    </div>;
};
